// Dibuja gráficas en un <canvas> y las regresa como PNG (base64) para poder
// incrustarlas como imágenes en el Excel (ExcelJS no soporta gráficas nativas
// de Excel, solo imágenes).

import { restarSeisHoras } from '../fechaMexico';

const COLOR = {
  azulOscuro: '#1F4E78',
  azul: '#2E75B6',
  celeste: '#9DC3E6',
  verde: '#2E7D32',
  naranja: '#E67E22',
  rojo: '#C0392B',
  gris: '#94A3B8',
  texto: '#1F2A37',
  textoSuave: '#64748B',
  grid: '#E2E8F0',
  borde: '#E2E8F0',
};

function colorPorOcupacion(porcentaje) {
  if (porcentaje >= 100) return COLOR.rojo;
  if (porcentaje >= 75) return COLOR.naranja;
  return COLOR.verde;
}

// Escala a 2x para que la imagen se vea nítida (tipo "retina") aunque se
// incruste a un tamaño de pantalla normal.
function crearContexto(ancho, alto) {
  const escala = 2;
  const canvas = document.createElement('canvas');
  canvas.width = ancho * escala;
  canvas.height = alto * escala;
  const ctx = canvas.getContext('2d');
  ctx.scale(escala, escala);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, ancho, alto);
  return ctx;
}

function dibujarMarco(ctx, ancho, alto) {
  ctx.save();
  ctx.strokeStyle = COLOR.borde;
  ctx.lineWidth = 1;
  redondearRect(ctx, 0.5, 0.5, ancho - 1, alto - 1, 10);
  ctx.stroke();
  ctx.restore();
}

function redondearRect(ctx, x, y, w, h, radio) {
  const r = Math.min(radio, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function dibujarTitulo(ctx, texto, ancho) {
  ctx.fillStyle = COLOR.azulOscuro;
  ctx.font = 'bold 16px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(texto, ancho / 2, 27);
}

// Bar con la esquina superior redondeada (crece desde el eje hacia arriba).
function barraArriba(ctx, x, yTop, w, h, color) {
  if (h <= 0 || w <= 0) return;
  const r = Math.min(5, w / 2, h);
  const g = ctx.createLinearGradient(0, yTop, 0, yTop + h);
  g.addColorStop(0, color.claro);
  g.addColorStop(1, color.oscuro);

  ctx.save();
  ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 2;
  ctx.beginPath();
  ctx.moveTo(x, yTop + h);
  ctx.lineTo(x, yTop + r);
  ctx.arcTo(x, yTop, x + r, yTop, r);
  ctx.lineTo(x + w - r, yTop);
  ctx.arcTo(x + w, yTop, x + w, yTop + r, r);
  ctx.lineTo(x + w, yTop + h);
  ctx.closePath();
  ctx.fillStyle = g;
  ctx.fill();
  ctx.restore();
}

// Barra horizontal con la esquina derecha redondeada.
function barraDerecha(ctx, x, y, w, h, color) {
  if (w <= 0 || h <= 0) return;
  const r = Math.min(5, h / 2, w);
  const g = ctx.createLinearGradient(x, 0, x + w, 0);
  g.addColorStop(0, color.oscuro);
  g.addColorStop(1, color.claro);

  ctx.save();
  ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
  ctx.shadowBlur = 5;
  ctx.shadowOffsetX = 2;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x, y + h);
  ctx.closePath();
  ctx.fillStyle = g;
  ctx.fill();
  ctx.restore();
}

function tonos(hex) {
  return { claro: aclarar(hex, 0.28), oscuro: hex };
}

function aclarar(hex, cantidad) {
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const mezclar = (canal) => Math.round(canal + (255 - canal) * cantidad);
  return `rgb(${mezclar(r)}, ${mezclar(g)}, ${mezclar(b)})`;
}

// Redondea el máximo del eje hacia arriba a un número "bonito" (1, 2, 2.5, 5, 10 x 10^n).
function maximoEscala(valor) {
  if (valor <= 0) return 10;
  const magnitud = 10 ** Math.floor(Math.log10(valor));
  const pasos = [1, 2, 2.5, 5, 10];
  for (const paso of pasos) {
    const candidato = paso * magnitud;
    if (candidato >= valor) return candidato;
  }
  return 10 * magnitud;
}

function truncar(texto, maxCaracteres) {
  if (!texto) return '';
  return texto.length > maxCaracteres ? `${texto.slice(0, maxCaracteres - 1)}…` : texto;
}

function toDataUrl(canvasCtx) {
  return canvasCtx.canvas.toDataURL('image/png');
}

/**
 * Gráfica de barras agrupadas por categoría (p. ej. Ocupados vs Libres por Categoría).
 */
export function graficaBarrasAgrupadas({ titulo, categorias, series, ancho = 480, alto = 300 }) {
  const ctx = crearContexto(ancho, alto);
  dibujarTitulo(ctx, titulo, ancho);

  const margen = { top: 46, bottom: 66, left: 42, right: 16 };
  const plotW = ancho - margen.left - margen.right;
  const plotH = alto - margen.top - margen.bottom;

  const maxValor = Math.max(1, ...series.flatMap((s) => s.valores));
  const maxEscala = maximoEscala(maxValor);
  const divisiones = 4;

  ctx.strokeStyle = COLOR.grid;
  ctx.fillStyle = COLOR.textoSuave;
  ctx.font = '10px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.lineWidth = 1;
  for (let i = 0; i <= divisiones; i += 1) {
    const y = margen.top + plotH - (i / divisiones) * plotH;
    ctx.beginPath();
    ctx.moveTo(margen.left, y);
    ctx.lineTo(margen.left + plotW, y);
    ctx.stroke();
    const valorLinea = Math.round((maxEscala * i) / divisiones);
    ctx.fillText(String(valorLinea), margen.left - 6, y + 3);
  }

  const n = Math.max(categorias.length, 1);
  const anchoSlot = plotW / n;
  const anchoBarra = Math.min(22, anchoSlot * 0.3);
  const espacio = anchoBarra * 0.35;
  const anchoGrupo = series.length * anchoBarra + (series.length - 1) * espacio;

  ctx.textAlign = 'center';
  categorias.forEach((categoria, i) => {
    const slotX = margen.left + i * anchoSlot + anchoSlot / 2;
    const inicioGrupo = slotX - anchoGrupo / 2;

    series.forEach((serie, j) => {
      const valor = serie.valores[i] ?? 0;
      const alturaBarra = (valor / maxEscala) * plotH;
      const x = inicioGrupo + j * (anchoBarra + espacio);
      const yTop = margen.top + plotH - alturaBarra;
      barraArriba(ctx, x, yTop, anchoBarra, alturaBarra, tonos(serie.color));

      if (valor > 0) {
        ctx.fillStyle = COLOR.texto;
        ctx.font = 'bold 9px "Segoe UI", Arial, sans-serif';
        ctx.fillText(String(valor), x + anchoBarra / 2, yTop - 4);
      }
    });

    ctx.fillStyle = COLOR.texto;
    ctx.font = '10px "Segoe UI", Arial, sans-serif';
    const etiqueta = truncar(categoria, 14);
    ctx.fillText(etiqueta, slotX, margen.top + plotH + 16);
  });

  // Eje base
  ctx.strokeStyle = COLOR.textoSuave;
  ctx.beginPath();
  ctx.moveTo(margen.left, margen.top + plotH);
  ctx.lineTo(margen.left + plotW, margen.top + plotH);
  ctx.stroke();

  // Leyenda
  const anchoLeyenda = series.reduce((acc, s) => acc + 16 + ctx.measureText(s.nombre).width + 20, 0);
  let xLeyenda = ancho / 2 - anchoLeyenda / 2;
  const yLeyenda = alto - 16;
  series.forEach((serie) => {
    ctx.fillStyle = serie.color;
    redondearRect(ctx, xLeyenda, yLeyenda - 8, 10, 10, 2);
    ctx.fill();
    ctx.fillStyle = COLOR.texto;
    ctx.textAlign = 'left';
    ctx.font = '10px "Segoe UI", Arial, sans-serif';
    ctx.fillText(serie.nombre, xLeyenda + 14, yLeyenda + 1);
    xLeyenda += 14 + ctx.measureText(serie.nombre).width + 20;
  });

  dibujarMarco(ctx, ancho, alto);
  return { dataUrl: toDataUrl(ctx), width: ancho, height: alto };
}

/**
 * Gráfica de barras horizontales (p. ej. top de materias con mayor demanda).
 */
export function graficaBarrasHorizontales({ titulo, filas, ancho = 480, alto = 300, sufijo = '%', colorFijo }) {
  const ctx = crearContexto(ancho, alto);
  dibujarTitulo(ctx, titulo, ancho);

  const margen = { top: 42, bottom: 12, left: 150, right: 40 };
  const plotW = ancho - margen.left - margen.right;
  const plotH = alto - margen.top - margen.bottom;

  const n = Math.max(filas.length, 1);
  const alturaFila = plotH / n;
  const alturaBarra = Math.min(18, alturaFila * 0.55);
  const maxEscala = Math.max(100, maximoEscala(Math.max(1, ...filas.map((f) => f.valor))));

  filas.forEach((fila, i) => {
    const y = margen.top + i * alturaFila + (alturaFila - alturaBarra) / 2;
    const anchoBarra = (fila.valor / maxEscala) * plotW;
    const color = colorFijo ? tonos(colorFijo) : tonos(colorPorOcupacion(fila.valor));

    ctx.fillStyle = COLOR.texto;
    ctx.font = '10px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(truncar(fila.etiqueta, 26), margen.left - 10, y + alturaBarra / 2 + 3);

    barraDerecha(ctx, margen.left, y, anchoBarra, alturaBarra, color);

    ctx.fillStyle = COLOR.texto;
    ctx.font = 'bold 10px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`${fila.valor}${sufijo}`, margen.left + anchoBarra + 6, y + alturaBarra / 2 + 3);
  });

  dibujarMarco(ctx, ancho, alto);
  return { dataUrl: toDataUrl(ctx), width: ancho, height: alto };
}

/**
 * Gráfica de dona con texto grande al centro y leyenda a la derecha.
 */
export function graficaDona({ titulo, segmentos, centroTitulo, centroSubtitulo, ancho = 460, alto = 280 }) {
  const ctx = crearContexto(ancho, alto);
  dibujarTitulo(ctx, titulo, ancho);

  const total = segmentos.reduce((acc, s) => acc + s.valor, 0);
  const cx = 128;
  const cy = alto / 2 + 14;
  const rExt = 82;
  const rInt = 50;

  if (total <= 0) {
    ctx.strokeStyle = COLOR.grid;
    ctx.lineWidth = rExt - rInt;
    ctx.beginPath();
    ctx.arc(cx, cy, (rExt + rInt) / 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = COLOR.textoSuave;
    ctx.font = '12px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Sin datos', cx, cy + 4);
  } else {
    let anguloInicio = -Math.PI / 2;
    segmentos.forEach((segmento) => {
      const barrido = (segmento.valor / total) * Math.PI * 2;
      if (barrido <= 0) return;
      const anguloFin = anguloInicio + barrido;
      ctx.save();
      ctx.shadowColor = 'rgba(15, 23, 42, 0.15)';
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctx.arc(cx, cy, rExt, anguloInicio, anguloFin, false);
      ctx.arc(cx, cy, rInt, anguloFin, anguloInicio, true);
      ctx.closePath();
      ctx.fillStyle = segmento.color;
      ctx.fill();
      ctx.restore();
      anguloInicio = anguloFin;
    });
  }

  if (centroTitulo) {
    ctx.fillStyle = COLOR.azulOscuro;
    ctx.font = 'bold 22px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(centroTitulo, cx, cy + 6);
  }
  if (centroSubtitulo) {
    ctx.fillStyle = COLOR.textoSuave;
    ctx.font = '10px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(centroSubtitulo, cx, cy + 22);
  }

  // Leyenda a la derecha del anillo
  let yLeyenda = cy - (segmentos.length - 1) * 11;
  const xLeyenda = cx + rExt + 24;
  segmentos.forEach((segmento) => {
    ctx.fillStyle = segmento.color;
    redondearRect(ctx, xLeyenda, yLeyenda - 7, 10, 10, 2);
    ctx.fill();
    ctx.fillStyle = COLOR.texto;
    ctx.font = 'bold 11px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(segmento.etiqueta, xLeyenda + 15, yLeyenda + 2);
    ctx.fillStyle = COLOR.textoSuave;
    ctx.font = '10px "Segoe UI", Arial, sans-serif';
    const porcentaje = total > 0 ? Math.round((segmento.valor / total) * 100) : 0;
    ctx.fillText(`${segmento.valor} (${porcentaje}%)`, xLeyenda + 15, yLeyenda + 15);
    yLeyenda += 34;
  });

  dibujarMarco(ctx, ancho, alto);
  return { dataUrl: toDataUrl(ctx), width: ancho, height: alto };
}

/**
 * Línea de tiempo (área rellena + puntos) para ver en qué momento se concentraron
 * más eventos, con el punto más alto resaltado como "pico".
 */
export function graficaLinea({ titulo, etiquetas, valores, ancho = 940, alto = 260, colorLinea = COLOR.azul }) {
  const ctx = crearContexto(ancho, alto);
  dibujarTitulo(ctx, titulo, ancho);

  const margen = { top: 42, bottom: 40, left: 42, right: 24 };
  const plotW = ancho - margen.left - margen.right;
  const plotH = alto - margen.top - margen.bottom;

  if (!valores || valores.length === 0) {
    ctx.fillStyle = COLOR.textoSuave;
    ctx.font = '12px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Sin datos suficientes para esta gráfica', ancho / 2, margen.top + plotH / 2);
    dibujarMarco(ctx, ancho, alto);
    return { dataUrl: toDataUrl(ctx), width: ancho, height: alto };
  }

  const maxEscala = maximoEscala(Math.max(1, ...valores));
  const divisiones = 4;

  ctx.strokeStyle = COLOR.grid;
  ctx.fillStyle = COLOR.textoSuave;
  ctx.font = '10px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.lineWidth = 1;
  for (let i = 0; i <= divisiones; i += 1) {
    const y = margen.top + plotH - (i / divisiones) * plotH;
    ctx.beginPath();
    ctx.moveTo(margen.left, y);
    ctx.lineTo(margen.left + plotW, y);
    ctx.stroke();
    ctx.fillText(String(Math.round((maxEscala * i) / divisiones)), margen.left - 6, y + 3);
  }

  const n = valores.length;
  const pasoX = n > 1 ? plotW / (n - 1) : 0;
  const puntos = valores.map((valor, i) => ({
    x: margen.left + i * pasoX,
    y: margen.top + plotH - (valor / maxEscala) * plotH,
    valor,
  }));

  const gradienteArea = ctx.createLinearGradient(0, margen.top, 0, margen.top + plotH);
  gradienteArea.addColorStop(0, 'rgba(46, 117, 182, 0.35)');
  gradienteArea.addColorStop(1, 'rgba(46, 117, 182, 0.03)');
  ctx.beginPath();
  ctx.moveTo(puntos[0].x, margen.top + plotH);
  puntos.forEach((p) => ctx.lineTo(p.x, p.y));
  ctx.lineTo(puntos[puntos.length - 1].x, margen.top + plotH);
  ctx.closePath();
  ctx.fillStyle = gradienteArea;
  ctx.fill();

  ctx.beginPath();
  puntos.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.strokeStyle = colorLinea;
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.save();
  ctx.shadowColor = 'rgba(15, 23, 42, 0.2)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;
  ctx.stroke();
  ctx.restore();

  const indicePico = valores.indexOf(Math.max(...valores));

  puntos.forEach((p, i) => {
    const esPico = i === indicePico && p.valor > 0;
    ctx.beginPath();
    ctx.arc(p.x, p.y, esPico ? 5.5 : 3, 0, Math.PI * 2);
    ctx.fillStyle = esPico ? COLOR.rojo : colorLinea;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#FFFFFF';
    ctx.stroke();

    if (p.valor > 0) {
      ctx.fillStyle = esPico ? COLOR.rojo : COLOR.texto;
      ctx.font = esPico ? 'bold 10px "Segoe UI", Arial, sans-serif' : '9px "Segoe UI", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(p.valor), p.x, p.y - 9);
    }

    ctx.fillStyle = COLOR.textoSuave;
    ctx.font = '9px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(etiquetas[i], p.x, margen.top + plotH + 16);
  });

  if (indicePico >= 0 && valores[indicePico] > 0) {
    ctx.fillStyle = COLOR.rojo;
    ctx.font = 'bold 11px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(
      `Hora pico: ${etiquetas[indicePico]} (${valores[indicePico]} registros)`,
      ancho / 2,
      alto - 8,
    );
  }

  dibujarMarco(ctx, ancho, alto);
  return { dataUrl: toDataUrl(ctx), width: ancho, height: alto };
}

export function generarGraficaOcupacionGeneral(resumen) {
  return graficaDona({
    titulo: 'Ocupación general de lugares',
    segmentos: [
      { etiqueta: 'Ocupados', valor: resumen.lugaresOcupados, color: COLOR.azul },
      { etiqueta: 'Libres', valor: resumen.lugaresLibres, color: COLOR.celeste },
    ],
    centroTitulo: `${resumen.porcentajeOcupacion}%`,
    centroSubtitulo: 'ocupación',
  });
}

export function generarGraficaTokens(tokens) {
  return graficaDona({
    titulo: 'Claves de desinscripción',
    segmentos: [
      { etiqueta: 'Usadas', valor: tokens.usados, color: COLOR.naranja },
      { etiqueta: 'Sin usar', valor: tokens.sinUsar, color: COLOR.verde },
    ],
    centroTitulo: String(tokens.total),
    centroSubtitulo: 'generadas',
  });
}

export function generarGraficaPorCategoria(porCategoria) {
  return graficaBarrasAgrupadas({
    titulo: 'Ocupación por categoría',
    categorias: porCategoria.map((c) => c.category),
    series: [
      { nombre: 'Ocupados', color: COLOR.azul, valores: porCategoria.map((c) => c.ocupados) },
      { nombre: 'Libres', color: COLOR.celeste, valores: porCategoria.map((c) => c.libres) },
    ],
  });
}

export function generarGraficaTopMaterias(porMateria, limite = 10) {
  const filas = [...porMateria]
    .sort((a, b) => b.porcentajeOcupacion - a.porcentajeOcupacion)
    .slice(0, limite)
    .map((m) => ({ etiqueta: `${m.code} · ${m.name}`, valor: m.porcentajeOcupacion }));

  return graficaBarrasHorizontales({
    titulo: `Top ${filas.length} materias con mayor demanda`,
    filas,
    sufijo: '%',
  });
}

// Ventana de inscripción: fija en 8:30 am - 5:00 pm (nunca cambia), aunque queda
// como parámetro por si algún periodo futuro sí la mueve.
const VENTANA_INSCRIPCION_INICIO_MIN = 8 * 60 + 30; // 08:30
const VENTANA_INSCRIPCION_CIERRE_MIN = 17 * 60; // 17:00
const PASO_MEDIA_HORA_MIN = 30;

function minutosDeRegistro(fechaRegistro) {
  const ajustada = restarSeisHoras(fechaRegistro);
  const match = ajustada && ajustada.match(/ (\d{2}):(\d{2}):/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function formatearMinutosComoHora(totalMinutos) {
  const h = Math.floor(totalMinutos / 60);
  const m = totalMinutos % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Línea de tiempo de a qué hora del día se concentraron más inscripciones,
 * en bloques de media hora, sobre la ventana fija de inscripción (8:30-17:00,
 * ya ajustada a hora de México con restarSeisHoras). Los registros fuera de la
 * ventana (si los hay) no se grafican.
 */
export function generarGraficaHorasPico(
  registrados,
  inicioMin = VENTANA_INSCRIPCION_INICIO_MIN,
  cierreMin = VENTANA_INSCRIPCION_CIERRE_MIN,
) {
  const numBloques = Math.round((cierreMin - inicioMin) / PASO_MEDIA_HORA_MIN) + 1;
  const conteo = new Array(numBloques).fill(0);

  registrados.forEach((r) => {
    const minutos = minutosDeRegistro(r.fechaRegistro);
    if (minutos === null) return;
    const indice = Math.round((minutos - inicioMin) / PASO_MEDIA_HORA_MIN);
    if (indice >= 0 && indice < numBloques) conteo[indice] += 1;
  });

  const etiquetas = conteo.map((_, i) => formatearMinutosComoHora(inicioMin + i * PASO_MEDIA_HORA_MIN));

  return graficaLinea({
    titulo: 'Horas con más inscripciones',
    etiquetas,
    valores: conteo,
    ancho: 940,
    alto: 260,
  });
}
